import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { LogOut, Mail, Phone, Building2, RefreshCw, Trash2, Check, Archive } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";

const STATUS_COLORS = {
  new: "bg-brand-gold/15 text-brand-gold border-brand-gold/40",
  reviewed: "bg-brand-sage/20 text-brand-green border-brand-green/30",
  archived: "bg-slate-200 text-slate-600 border-slate-300",
};

const SERVICE_LABEL = {
  consulting: "Consulting",
  peer_support: "Peer Support",
  partnership: "Partnership",
  workshop: "Workshop",
  other: "Other",
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      if (active?.id === id) setActive({ ...active, status });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setActive(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === "new").length,
    reviewed: items.filter((i) => i.status === "reviewed").length,
    archived: items.filter((i) => i.status === "archived").length,
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-brand-green/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-green flex items-center justify-center text-brand-gold font-serif">K</div>
            <div>
              <div className="font-serif text-lg text-brand-green leading-none">KBX Admin</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-brand-sage">Submissions</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.email}</span>
            <button onClick={load} data-testid="admin-refresh" className="p-2 rounded-full border border-brand-green/20 text-brand-green hover:bg-brand-mist" aria-label="refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-brand-green text-white rounded-full px-4 py-2 text-sm hover:bg-brand-navy"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-brand-sage">Dashboard</div>
            <h1 className="font-serif text-4xl text-brand-green mt-1">Contact submissions</h1>
            <p className="text-slate-600 mt-2 text-sm">Review and manage incoming consultation and contact requests.</p>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="admin-filters">
            {["all", "new", "reviewed", "archived"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                  filter === s
                    ? "bg-brand-green text-white border-brand-green"
                    : "bg-white text-slate-700 border-brand-green/20 hover:bg-brand-mist"
                }`}
              >
                {s} <span className="ml-1 opacity-70">({counts[s]})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-white border border-brand-green/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500" data-testid="admin-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="admin-empty">No submissions yet.</div>
          ) : (
            <Table data-testid="admin-submissions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Interest</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-brand-mist/40" onClick={() => setActive(s)} data-testid={`submission-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <div className="text-brand-green">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </TableCell>
                    <TableCell>{SERVICE_LABEL[s.service_interest] || s.service_interest}</TableCell>
                    <TableCell className="text-slate-700">{s.organization || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{s.stage || "—"}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(s.submitted_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`uppercase text-[10px] tracking-[0.15em] ${STATUS_COLORS[s.status]}`}>
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button className="text-brand-green text-sm font-medium hover:underline">View</button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <div className="text-xs tracking-[0.25em] uppercase text-brand-sage">Submission</div>
                <DialogTitle className="font-serif text-3xl text-brand-green">{active.name}</DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {active.email}</span>
                  {active.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {active.phone}</span>}
                  {active.organization && <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {active.organization}</span>}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="rounded-lg bg-brand-mist/50 p-4">
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-sage">Interest</div>
                  <div className="mt-1 text-brand-green font-medium">{SERVICE_LABEL[active.service_interest] || active.service_interest}</div>
                </div>
                <div className="rounded-lg bg-brand-mist/50 p-4">
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-sage">Stage</div>
                  <div className="mt-1 text-brand-green font-medium">{active.stage || "—"}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs tracking-[0.2em] uppercase text-brand-sage">Message</div>
                <p className="mt-2 text-slate-700 whitespace-pre-wrap leading-relaxed">{active.message}</p>
              </div>

              <div className="text-xs text-slate-500 mt-4">
                Submitted {new Date(active.submitted_at).toLocaleString()}
              </div>

              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                <button onClick={() => setStatus(active.id, "reviewed")} data-testid="dialog-mark-reviewed" className="inline-flex items-center gap-1.5 bg-brand-green text-white rounded-full px-4 py-2 text-xs hover:bg-brand-navy">
                  <Check className="w-3.5 h-3.5" /> Mark Reviewed
                </button>
                <button onClick={() => setStatus(active.id, "archived")} data-testid="dialog-archive" className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 rounded-full px-4 py-2 text-xs hover:bg-slate-200">
                  <Archive className="w-3.5 h-3.5" /> Archive
                </button>
                <button onClick={() => setStatus(active.id, "new")} data-testid="dialog-mark-new" className="inline-flex items-center gap-1.5 bg-brand-mist text-brand-green rounded-full px-4 py-2 text-xs hover:bg-brand-mist/70">
                  Mark New
                </button>
                <div className="flex-1" />
                <button onClick={() => remove(active.id)} data-testid="dialog-delete" className="inline-flex items-center gap-1.5 text-red-600 rounded-full px-4 py-2 text-xs hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
